import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Package, Plus, Edit, Trash2, Eye, EyeOff, ChevronLeft, TrendingUp, ShoppingBag, DollarSign, AlertCircle, RefreshCw } from 'lucide-react'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { apiService } from '../../services/apiService'

const KataleMyProducts = () => {
  const { customer } = useCustomerAuth()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [deletingId, setDeletingId] = useState(null)
  const [togglingId, setTogglingId] = useState(null)
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    totalSales: 0,
    totalRevenue: 0
  })

  useEffect(() => {
    if (customer) {
      fetchProducts()
    }
  }, [customer])

  // Helper to extract MG code from tenant DB (e.g., kololo_mg from mgfina6_tenant_kololo_mg)
  const getMgCode = (tenantDb) => {
    if (!tenantDb) return null
    const prefix = 'mgfina6_tenant_'
    if (tenantDb.startsWith(prefix)) {
      return tenantDb.substring(prefix.length)
    }
    const parts = tenantDb.split('_')
    return parts.length > 1 ? parts[1] : tenantDb
  }

  const fetchProducts = async () => {
    if (!customer) return
    
    const sellerMemNo = customer.memberNo
    const sellerMgCode = getMgCode(customer.tenantDb)
    
    if (!sellerMemNo || !sellerMgCode) return
    
    setLoading(true)
    setError('')
    
    try {
      const response = await apiService.getKataleMyProducts(sellerMemNo, sellerMgCode)
      
      if (response.status === 'SUCCESS' && response.data) {
        const productList = response.data || []
        setProducts(productList)
        
        // Calculate stats
        setStats({
          totalProducts: productList.length,
          activeProducts: productList.filter(p => p.status === 'active').length,
          totalSales: productList.reduce((sum, p) => sum + (p.sales_count || 0), 0),
          totalRevenue: productList.reduce((sum, p) => sum + (p.total_revenue || 0), 0)
        })
      } else {
        setError(response.message || 'Failed to load products')
      }
    } catch (err) {
      console.error('Failed to fetch products:', err)
      setError('Failed to load products. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const toggleProductStatus = async (productId) => {
    const product = products.find(p => p.id === productId)
    if (!product) return
    
    const newStatus = product.status === 'active' ? 'inactive' : 'active'
    setTogglingId(productId)
    
    try {
      const response = await apiService.updateKataleProduct(productId, { status: newStatus })
      if (response.status === 'SUCCESS') {
        setProducts(products.map(p => 
          p.id === productId ? { ...p, status: newStatus } : p
        ))
        setStats(prev => ({
          ...prev,
          activeProducts: newStatus === 'active' ? prev.activeProducts + 1 : prev.activeProducts - 1
        }))
      } else {
        alert(response.message || 'Failed to update product status')
      }
    } catch (err) {
      console.error('Failed to toggle status:', err)
      alert('Failed to update product status')
    } finally {
      setTogglingId(null)
    }
  }

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return
    
    setDeletingId(productId)
    
    try {
      const response = await apiService.deleteKataleProduct(productId)
      if (response.status === 'SUCCESS') {
        setProducts(products.filter(p => p.id !== productId))
        setStats(prev => ({
          ...prev,
          totalProducts: prev.totalProducts - 1,
          activeProducts: products.find(p => p.id === productId)?.status === 'active' 
            ? prev.activeProducts - 1 
            : prev.activeProducts
        }))
      } else {
        alert(response.message || 'Failed to delete product')
      }
    } catch (err) {
      console.error('Failed to delete product:', err)
      alert('Failed to delete product')
    } finally {
      setDeletingId(null)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const getStatusBadge = (status, quantity) => {
    if (quantity <= 0) {
      return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">Out of Stock</span>
    }
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">Active</span>
      case 'inactive':
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">Inactive</span>
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">{status}</span>
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/katale" className="text-gray-600 hover:text-gray-900 mr-4">
                <ChevronLeft className="h-5 w-5" />
              </Link>
              <Package className="h-6 w-6 text-purple-600 mr-2" />
              <h1 className="text-2xl font-bold text-gray-900">My Products</h1>
            </div>
            <div className="flex gap-2">
              <button onClick={fetchProducts} className="btn btn-secondary btn-sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <Link to="/katale/add-product" className="btn btn-primary">
                <Plus className="h-5 w-5 mr-2" />
                Add Product
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
            <button onClick={fetchProducts} className="ml-auto text-red-700 hover:text-red-900 underline">
              Retry
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-purple-100 mr-3">
                <Package className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Products</p>
                <p className="text-xl font-bold text-gray-900">{stats.totalProducts}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-blue-100 mr-3">
                <Eye className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Listings</p>
                <p className="text-xl font-bold text-gray-900">{stats.activeProducts}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-blue-100 mr-3">
                <ShoppingBag className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Sales</p>
                <p className="text-xl font-bold text-gray-900">{stats.totalSales}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-yellow-100 mr-3">
                <DollarSign className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Products List */}
        {products.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No products yet</h2>
            <p className="text-gray-600 mb-6">Start selling by adding your first product.</p>
            <Link to="/katale/add-product" className="btn btn-primary">
              <Plus className="h-5 w-5 mr-2" />
              Add Your First Product
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sales</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center mr-4">
                            {product.primary_image ? (
                              <img src={product.primary_image} alt={product.name} className="w-full h-full object-cover rounded-lg" />
                            ) : (
                              <Package className="h-6 w-6 text-gray-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{product.name}</p>
                            <p className="text-sm text-gray-500">{product.category_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{formatCurrency(product.price)}</span>
                        <span className="text-gray-500 text-sm">/{product.unit || 'unit'}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`font-medium ${product.quantity === 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {product.quantity} {product.unit || 'unit'}s
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                        {product.sales_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-600">
                        {formatCurrency(product.total_revenue || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(product.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => toggleProductStatus(product.id)}
                            disabled={togglingId === product.id}
                            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                            title={product.status === 'active' ? 'Deactivate' : 'Activate'}
                          >
                            {togglingId === product.id ? (
                              <RefreshCw className="h-5 w-5 animate-spin" />
                            ) : product.status === 'active' ? (
                              <EyeOff className="h-5 w-5" />
                            ) : (
                              <Eye className="h-5 w-5" />
                            )}
                          </button>
                          <Link
                            to={`/katale/edit-product/${product.id}`}
                            className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-100"
                            title="Edit"
                          >
                            <Edit className="h-5 w-5" />
                          </Link>
                          <button
                            onClick={() => deleteProduct(product.id)}
                            disabled={deletingId === product.id}
                            className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                            title="Delete"
                          >
                            {deletingId === product.id ? (
                              <RefreshCw className="h-5 w-5 animate-spin" />
                            ) : (
                              <Trash2 className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Help Banner */}
        <div className="mt-8 bg-purple-50 border border-purple-200 rounded-lg p-6">
          <h3 className="font-semibold text-purple-800 mb-2">📦 Tips for Selling</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-purple-700">
            <div>
              <strong>Quality Photos</strong>
              <p>Products with clear photos sell 3x faster.</p>
            </div>
            <div>
              <strong>Competitive Pricing</strong>
              <p>Research market prices to attract more buyers.</p>
            </div>
            <div>
              <strong>Stock Updates</strong>
              <p>Keep your stock levels accurate to avoid cancellations.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default KataleMyProducts
